import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
    Calendar, FileSpreadsheet, FileText, Edit, Trash2,
    Check, Clock, XCircle, Loader2, ChevronDown, ChevronRight,
    Wine, Coffee, Utensils, Waves, Umbrella, Music, Building, Package, Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAR_CONFIG = {
    'lobby': { name: 'Lobby Bar', icon: Coffee, color: 'bg-amber-500' },
    'servis': { name: 'Servis Bar', icon: Utensils, color: 'bg-blue-500' },
    'vitamin': { name: 'Vitamin Bar', icon: Wine, color: 'bg-green-500' },
    'havuz': { name: 'Havuz Bar', icon: Waves, color: 'bg-cyan-500' },
    'plaj': { name: 'Plaj Bar', icon: Umbrella, color: 'bg-orange-500' },
    'amfi': { name: 'Amfi Bar', icon: Music, color: 'bg-purple-500' },
    'teras': { name: 'Teras Bar', icon: Building, color: 'bg-slate-600' },
    'alacarte': { name: 'Alacarte Bar', icon: Wine, color: 'bg-rose-600' },
    'minibar': { name: 'Minibar', icon: Coffee, color: 'bg-emerald-600' },
};

const STATUS_CONFIG = {
    'pending': { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
    'approved': { label: 'Onaylandi', color: 'bg-green-100 text-green-800 border-green-200', icon: Check },
    'rejected': { label: 'Reddedildi', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

export function Depo() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [expandedBar, setExpandedBar] = useState(null);
    const [editDialog, setEditDialog] = useState(false);
    const [editRequest, setEditRequest] = useState(null);
    const [editItems, setEditItems] = useState([]);
    const [editStatus, setEditStatus] = useState('pending');
    const [editNotes, setEditNotes] = useState('');

    const getToken = () => localStorage.getItem('token');

    useEffect(() => { fetchRequests(); }, [filterDate]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/api/bar-requests?`;
            if (filterDate) url += `date=${filterDate}`;
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${getToken()}` } });
            if (res.ok) setRequests(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    // Group requests by bar
    const groupedByBar = {};
    requests.forEach(req => {
        if (!groupedByBar[req.bar_id]) groupedByBar[req.bar_id] = [];
        groupedByBar[req.bar_id].push(req);
    });

    const openEdit = (req) => {
        setEditRequest(req);
        setEditItems([...req.items]);
        setEditStatus(req.status || 'pending');
        setEditNotes(req.notes || '');
        setEditDialog(true);
    };

    const saveEdit = async () => {
        try {
            const res = await fetch(`${API_URL}/api/bar-requests/${editRequest.id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: editItems, status: editStatus, notes: editNotes })
            });
            if (res.ok) { toast.success('Talep guncellendi'); setEditDialog(false); fetchRequests(); }
            else { const err = await res.json(); toast.error(err.detail || 'Hata'); }
        } catch { toast.error('Guncelleme hatasi'); }
    };

    const deleteRequest = async (id) => {
        if (!window.confirm('Bu talebi silmek istediginize emin misiniz?')) return;
        try {
            const res = await fetch(`${API_URL}/api/bar-requests/${id}`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                toast.success('Talep silindi');
                // Update state locally instead of full reload to avoid UI flash
                setRequests(prev => prev.filter(r => r.id !== id));
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Silme basarisiz');
            }
        } catch { toast.error('Silme hatasi'); }
    };

    const exportExcel = async () => {
        try {
            const res = await fetch(`${API_URL}/api/bar-requests/export/excel?date=${filterDate}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `talepler_${filterDate}.xlsx`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
            }
        } catch { toast.error('Export hatasi'); }
    };

    const exportPDF = async () => {
        try {
            const res = await fetch(`${API_URL}/api/bar-requests/export/pdf?date=${filterDate}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `talepler_${filterDate}.pdf`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
            }
        } catch { toast.error('Export hatasi'); }
    };

    const totalRequests = requests.length;
    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <div className="space-y-6" data-testid="depo-page">
            <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Depo</h1>
                <p className="text-slate-500 text-sm mt-1">Barlardan gelen urun talepleri</p>
            </div>

            {/* Filter & Export */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)}
                                className="w-40 h-9" data-testid="depo-filter-date" />
                        </div>
                        <Badge variant="outline" className="text-sm">
                            {totalRequests} talep | {pendingCount} beklemede
                        </Badge>
                        <div className="ml-auto flex gap-2">
                            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="depo-export-excel">
                                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                            </Button>
                            <Button variant="outline" size="sm" onClick={exportPDF} data-testid="depo-export-pdf">
                                <FileText className="w-4 h-4 mr-1" /> PDF
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Bar Cards */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
            ) : Object.keys(groupedByBar).length === 0 ? (
                <Card className="bg-slate-50">
                    <CardContent className="p-8 text-center text-slate-400">
                        <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Bu tarih icin talep bulunamadi</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {Object.entries(groupedByBar).map(([barId, barRequests]) => {
                        const config = BAR_CONFIG[barId] || { name: barId, icon: Package, color: 'bg-slate-500' };
                        const BarIcon = config.icon;
                        const isExpanded = expandedBar === barId;
                        const barPending = barRequests.filter(r => r.status === 'pending').length;

                        return (
                            <Card key={barId} className="overflow-hidden">
                                {/* Bar Header - Clickable */}
                                <div
                                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                                    onClick={() => setExpandedBar(isExpanded ? null : barId)}
                                    data-testid={`depo-bar-${barId}`}
                                >
                                    <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center flex-shrink-0`}>
                                        <BarIcon className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-slate-800">{config.name}</h3>
                                        <p className="text-xs text-slate-500">
                                            {barRequests.length} talep
                                            {barPending > 0 && <span className="text-yellow-600 ml-2">{barPending} beklemede</span>}
                                        </p>
                                    </div>
                                    {isExpanded
                                        ? <ChevronDown className="w-5 h-5 text-slate-400" />
                                        : <ChevronRight className="w-5 h-5 text-slate-400" />
                                    }
                                </div>

                                {/* Expanded Requests */}
                                {isExpanded && (
                                    <div className="border-t divide-y">
                                        {barRequests.map((req) => {
                                            const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG['pending'];
                                            const StatusIcon = sc.icon;
                                            const isSedna = req.source === 'sedna';
                                            return (
                                                <div key={req.id} className="p-4 hover:bg-slate-50/50">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            {isSedna ? (
                                                                <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300 text-xs" data-testid={`depo-sedna-badge-${req.id}`}>
                                                                    Sedna (Otomatik)
                                                                </Badge>
                                                            ) : (
                                                                <Badge className={`${sc.color} border text-xs`}>
                                                                    <StatusIcon className="w-3 h-3 mr-1" />
                                                                    {sc.label}
                                                                </Badge>
                                                            )}
                                                            <span className="text-xs text-slate-400">
                                                                {req.created_at ? new Date(req.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                                            </span>
                                                            {req.created_by_name && (
                                                                <span className="text-xs text-slate-400">- {req.created_by_name}</span>
                                                            )}
                                                        </div>
                                                        {!isSedna && (
                                                            <div className="flex items-center gap-1">
                                                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                                                    onClick={(e) => { e.stopPropagation(); openEdit(req); }}
                                                                    data-testid={`edit-req-${req.id}`}>
                                                                    <Edit className="w-3.5 h-3.5 text-slate-500" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                                                    onClick={(e) => { e.stopPropagation(); deleteRequest(req.id); }}
                                                                    data-testid={`delete-req-${req.id}`}>
                                                                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="space-y-1">
                                                        {req.items?.map((item, i) => (
                                                            <div key={i} className="flex items-center gap-3 text-sm">
                                                                <span className="text-slate-700">{item.product_name}</span>
                                                                <Badge variant="outline" className="text-xs">
                                                                    {item.quantity} {item.unit}
                                                                </Badge>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {req.notes && (
                                                        <p className="text-xs text-slate-500 mt-2 italic">Not: {req.notes}</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={editDialog} onOpenChange={setEditDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Talep Duzenle</DialogTitle>
                    </DialogHeader>
                    {editRequest && (
                        <div className="space-y-4">
                            <div className="text-sm text-slate-500">
                                {BAR_CONFIG[editRequest.bar_id]?.name} | {editRequest.date}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Urunler</label>
                                {editItems.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <Input value={item.product_name} className="h-9 flex-1"
                                            onChange={(e) => { const ni = [...editItems]; ni[idx].product_name = e.target.value; setEditItems(ni); }} />
                                        <Select value={item.unit || 'adet'} onValueChange={(v) => { const ni = [...editItems]; ni[idx].unit = v; setEditItems(ni); }}>
                                            <SelectTrigger className="w-24 h-9"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="adet">Adet</SelectItem>
                                                <SelectItem value="cl">CL</SelectItem>
                                                <SelectItem value="litre">Litre</SelectItem>
                                                <SelectItem value="koli">Koli</SelectItem>
                                                <SelectItem value="box">Box</SelectItem>
                                                <SelectItem value="paket">Paket</SelectItem>
                                                <SelectItem value="kg">KG</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input type="number" value={item.quantity || ''} className="h-9 w-20 text-center"
                                            onChange={(e) => { const ni = [...editItems]; ni[idx].quantity = parseFloat(e.target.value) || 0; setEditItems(ni); }} />
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400"
                                            onClick={() => {
                                                if (editItems.length <= 1) {
                                                    if (window.confirm('Son kalem siliniyor. Talebin tamami silinsin mi?')) {
                                                        deleteRequest(editRequest.id);
                                                        setEditDialog(false);
                                                    }
                                                } else {
                                                    setEditItems(editItems.filter((_, i) => i !== idx));
                                                }
                                            }}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                ))}
                                <Button variant="outline" size="sm" onClick={() => setEditItems([...editItems, { product_name: '', unit: 'adet', quantity: 0 }])}>
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Kalem Ekle
                                </Button>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Durum</label>
                                <Select value={editStatus} onValueChange={setEditStatus}>
                                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">Beklemede</SelectItem>
                                        <SelectItem value="approved">Onaylandi</SelectItem>
                                        <SelectItem value="rejected">Reddedildi</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Notlar</label>
                                <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Not ekle..." className="h-9 mt-1" />
                            </div>
                            <div className="flex gap-2 justify-between">
                                <Button variant="destructive" size="sm" onClick={() => {
                                    deleteRequest(editRequest.id);
                                    setEditDialog(false);
                                }} data-testid="dialog-delete-request">
                                    <Trash2 className="w-4 h-4 mr-1" /> Talebi Sil
                                </Button>
                                <div className="flex gap-2">
                                    <Button variant="outline" onClick={() => setEditDialog(false)}>Iptal</Button>
                                    <Button onClick={saveEdit}>Kaydet</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Depo;
